/**
 * TurnstileContext
 * 
 * Provides global state management for Cloudflare Turnstile verification.
 * Shows an inline modal when re-verification is needed instead of redirecting to a separate page.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Modal } from 'antd';
import Turnstile from 'react-turnstile';

interface TurnstileContextValue {
  /** Request turnstile verification. Returns a promise that resolves with the token. */
  requestVerification: () => Promise<string>;
  /** Check if turnstile token exists and is not expired (8 hours) */
  isTokenValid: () => boolean;
  /** Get the current turnstile token */
  getToken: () => string | null;
}

const TurnstileContext = createContext<TurnstileContextValue | null>(null);

const TURNSTILE_EXPIRY_MS = 8 * 60 * 60 * 1000; // 8 hours

export function TurnstileProvider({ children }: { children: React.ReactNode }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<((token: string) => void) | null>(null);

  const isTokenValid = useCallback((): boolean => {
    const token = localStorage.getItem('turnstileToken');
    const lastVerifiedAt = localStorage.getItem('turnstileLastVerifiedAt');
    
    if (!token || !lastVerifiedAt) {
      return false;
    }

    const verifiedTime = new Date(lastVerifiedAt).getTime();
    const now = Date.now();
    return now - verifiedTime < TURNSTILE_EXPIRY_MS;
  }, []);

  const getToken = useCallback((): string | null => {
    return localStorage.getItem('turnstileToken');
  }, []);

  const requestVerification = useCallback((): Promise<string> => {
    // If token is still valid, return it immediately
    if (isTokenValid()) {
      return Promise.resolve(localStorage.getItem('turnstileToken')!);
    }

    // Show modal and return promise that resolves when verification completes
    return new Promise((resolve) => {
      setResolvePromise(() => resolve);
      setIsModalVisible(true);
    });
  }, [isTokenValid]);

  const handleVerify = useCallback((token: string) => {
    localStorage.setItem('turnstileToken', token);
    localStorage.setItem('turnstileLastVerifiedAt', new Date().toISOString());
    setIsModalVisible(false);
    
    if (resolvePromise) {
      resolvePromise(token);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  const siteKey = (window as any).env?.TURNSTILE_SITE_KEY;

  // Set global handler for use outside React components (e.g., HTTP interceptors)
  useEffect(() => {
    setGlobalTurnstileHandler(requestVerification);
    return () => {
      globalRequestVerification = null;
    };
  }, [requestVerification]);

  return (
    <TurnstileContext.Provider value={{ requestVerification, isTokenValid, getToken }}>
      {children}
      {siteKey && (
        <Modal
          open={isModalVisible}
          footer={null}
          closable={false}
          centered
          maskClosable={false}
          width={350}
          styles={{ body: { textAlign: 'center', padding: '24px' } }}
        >
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>Security Verification</h3>
            <p style={{ color: '#666', marginTop: 8 }}>
              Please complete the verification to continue.
            </p>
          </div>
          <Turnstile
            sitekey={siteKey}
            onVerify={handleVerify}
          />
        </Modal>
      )}
    </TurnstileContext.Provider>
  );
}

export function useTurnstile(): TurnstileContextValue {
  const context = useContext(TurnstileContext);
  if (!context) {
    throw new Error('useTurnstile must be used within a TurnstileProvider');
  }
  return context;
}

/**
 * Trigger turnstile verification from outside React components (e.g., HTTP interceptors).
 * This is set by TurnstileProvider on mount.
 */
let globalRequestVerification: (() => Promise<string>) | null = null;

export function setGlobalTurnstileHandler(handler: () => Promise<string>) {
  globalRequestVerification = handler;
}

export function triggerTurnstileVerification(): Promise<string> {
  if (!globalRequestVerification) {
    return Promise.reject(new Error('TurnstileProvider not mounted'));
  }
  return globalRequestVerification();
}

